import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

// The /integrations surface: the per-account integration operation queue +
// 30-day history. Mirrors inrange_agents.integration_routes on the BE. One list
// poll feeds the whole page (principle 31 - never N per-item polls); a separate
// cheap count poll feeds the navbar badge.

export type IntegrationProvider = "gmail" | "drive" | "slack";
export type IntegrationStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export interface IntegrationOp {
  id: string;
  provider: IntegrationProvider;
  // sync | pending_decision | folder_sync ...
  kind: string;
  project_id: string;
  // Resolved BE-side (batched); null for a legacy / deleted project, in which
  // case the page falls back to the project_id.
  project_name: string | null;
  // The Google mailbox principal - the per-account serialization key.
  account_email: string;
  status: IntegrationStatus;
  // Upstream API calls the op made (display only; 0 until the limiter chokepoint
  // wiring lands - see the design doc).
  call_count: number;
  requested_by_email: string | null;
  error: string | null;
  // One-line summary of what a finished op did (e.g. "16 added, 0 changed,
  // 2 removed"); null until finished. The pulled files then extract separately.
  detail: string | null;
  enqueued_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface IntegrationsList {
  queued: IntegrationOp[];
  running: IntegrationOp[];
  completed: IntegrationOp[];
}

const LIST_KEY = QK.integrationsList;
const COUNT_KEY = QK.integrationsCount;

// Poll cadence: the queue moves on the timescale of a Gmail/Drive sync (seconds
// to a few minutes), so a 5s refetch keeps the OPEN integrations page live
// without hammering the endpoint.
const POLL_MS = 5_000;
// The navbar badge only needs a coarse "any outstanding ops?" signal, and it
// polls from EVERY page (mounted in AppLayout) rather than only while the
// integrations page is open. Poll it 6x less often (30s) - the badge can lag a
// few tens of seconds without the operator noticing, and this keeps the
// always-mounted poll off the API Lambda's hot path.
const COUNT_POLL_MS = 30_000;

export const useIntegrationsList = () => {
  const http = useAgentsHttp();
  return useQuery<IntegrationsList>(
    [LIST_KEY],
    () => http<IntegrationsList>("/integrations/list", {}),
    { refetchInterval: POLL_MS, refetchOnWindowFocus: true },
  );
};

export interface IntegrationsCount {
  outstanding: number;
}

export const useIntegrationsCount = (options?: { enabled?: boolean }) => {
  const http = useAgentsHttp();
  return useQuery<IntegrationsCount>(
    [COUNT_KEY],
    () => http<IntegrationsCount>("/integrations/count", {}),
    {
      enabled: options?.enabled ?? true,
      refetchInterval: COUNT_POLL_MS,
      refetchOnWindowFocus: true,
    },
  );
};

// Cancel a QUEUED op (running ops run to completion - v1 has no mid-fetch
// abort). The BE 409s a non-queued id; the caller surfaces the message. On
// success we invalidate the list + count so the row drops and the badge ticks
// down immediately rather than waiting for the next poll.
export const useCancelIntegration = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ id: string; status: string }, Error, string>(
    (id: string) => http<{ id: string; status: string }>("/integrations/cancel", { id }),
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [LIST_KEY] });
        qc.invalidateQueries({ queryKey: [COUNT_KEY] });
      },
    },
  );
};
